import React from 'react';
import classNames from 'classnames';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { useApi } from '../../context/api-context/use-api';
import { RicosEditorLazy } from '../ricos';
import { RatingsInput } from 'wix-ui-tpa/cssVars';
import { StarIcon } from '../icons/star-icon';
import { useContentForm } from '../../hooks/use-content-form';
import { COMMENT_BOX, COMMENT_BOX_PLACEHOLDER, LIST_ITEM, REPLY, TOP_LEVEL, RATING_INPUT, } from '../../../common/constants/data-hooks';
import ListItemContainer from '../list-item-container/list-item-container';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { withEditorContext, } from 'wix-rich-content-editor-common/libs/EditorEventsContext';
import { useNavigationPrompt } from '../../hooks/use-navigation-prompt';
import { useCancelFlow, useCancelFlowExecutor } from '../../hooks/use-cancel-flow';
import { getIsMobile } from '../../../common/store/base-params/base-params-selectors';
import { useModal } from '../modals';
import AnonymousPublishSheetModal from '../modals/anonymous-publish-sheet-modal/anonymous-publish-sheet-modal';
import { ControlButtons } from './control-buttons/control-buttons';
import { waitForAnimationEnd, getExternalToolbarId, getPlaceholderKey, formatLabel, } from './comment-box.utils';
import { PlaceholderText } from './placeholder-text/placeholder-text';
import { getCurrentMember } from '../../../common/store/members/members-selectors';
import { CommentBoxInnerFooter, CommentBoxOuterFooter } from './footers';
import { st, classes } from './comment-box.st.css';
const CommentBox = ({ onSubmit, onCancel, formName, className, mountExpanded = false, initialContent, isEdit, isReply, isInList, id, dataHook, onTyping, withGuestFlow, editorEvents, commentId, isMarked = false, forwardRef, rating, ratingLabel, onRatingChange, }) => {
    const { resourceId, placeholderText: customPlaceholderText, publishButtonText, } = useConfiguration();
    const [isExpanded, setIsExpanded] = React.useState(() => mountExpanded);
    const [interactionState, setInteractionState] = React.useState('default');
    const { currentMember, biClickPublishSelectBtn, biInitiateImageUpload, biInitiateFileUpload, biCommentFormExpanded, isMobile, } = useApi((state, actions) => ({
        currentMember: getCurrentMember(state),
        biClickPublishSelectBtn: actions.biClickPublishSelectBtn,
        biCommentFormExpanded: actions.biCommentFormExpanded,
        isMobile: getIsMobile(state),
        biInitiateImageUpload: actions.biInitiateImageUpload,
        biInitiateFileUpload: actions.biInitiateFileUpload,
    }));
    const autoFocus = React.useRef(true);
    const editorRef = React.useRef();
    const containerRef = React.useRef(null);
    const t = useTranslate();
    const contentForm = useContentForm({ initialContent, onTyping });
    const showModal = useModal();
    const validateRating = (value) => (isReply ? true : rating?.isRequired ? !!value : true);
    const handleSubmit = async ({ asGuest }) => {
        const content = (await editorEvents.publish());
        autoFocus.current = false; // we only want to focus form input on initial mount and not after publish
        const _rating = (() => {
            if (rating) {
                return rating.value
                    ? { type: 'ADD', value: rating.value }
                    : rating.hasPrevious
                        ? { type: 'REMOVE' }
                        : undefined;
            }
            return undefined;
        })();
        onSubmit({
            content,
            rating: _rating,
        }, contentForm.reset, asGuest);
    };
    const resolveCancelFlow = useCancelFlow({
        onCancel: () => onCancel(contentForm.content, () => {
            contentForm.reset();
            setIsExpanded(false);
            autoFocus.current = true;
        }, contentForm.getIsEmpty()),
        isReply,
        id,
    });
    useNavigationPrompt({ resolveCancelFlow, formIsMounted: isExpanded });
    const handleCancel = useCancelFlowExecutor(resolveCancelFlow);
    React.useImperativeHandle(forwardRef, () => ({
        getElementRef: () => containerRef.current,
        expand: async (options) => {
            if (typeof options?.autoFocus !== 'undefined') {
                autoFocus.current = options.autoFocus;
            }
            setIsExpanded(true);
            biCommentFormExpanded({ resourceId });
            containerRef.current && (await waitForAnimationEnd(containerRef.current));
        },
        close: () => setIsExpanded(false),
        focus: () => {
            if (editorRef.current) {
                editorRef.current?.focus('end');
                return 'focus_initiated';
            }
            return 'not_ready';
        },
    }));
    const shouldShowRatingInput = (() => {
        if (!rating || isReply) {
            return false;
        }
        return isMobile ? isExpanded : true;
    })();
    const placeholderText = customPlaceholderText ??
        t(getPlaceholderKey({
            isExpanded,
            isMobile,
            isReply,
            withRating: !!rating,
        }));
    const renderPublishControls = () => {
        return (React.createElement(ControlButtons, { isEdit: isEdit, disabled: !(contentForm.getIsValid() && validateRating(rating?.value)), onSubmit: (dropdownAsGuest) => {
                if (withGuestFlow && isMobile) {
                    biClickPublishSelectBtn({ resourceId, isReply });
                    showModal((toggle) => (React.createElement(AnonymousPublishSheetModal, { toggle: toggle, onPublish: ({ asGuest }) => {
                            handleSubmit({ asGuest });
                        } })));
                }
                else {
                    handleSubmit({ asGuest: dropdownAsGuest ? dropdownAsGuest : false });
                }
            }, onCancel: handleCancel, withDropdown: withGuestFlow && !isMobile, onDropdownShow: () => biClickPublishSelectBtn({ resourceId, isReply }), publishButtonText: publishButtonText }));
    };
    const innerContent = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: st(classes.contentContainer, {
                isHighlighted: interactionState !== 'default',
                isExpanded,
            }), onMouseEnter: () => setInteractionState((s) => (s === 'focus' ? s : 'hover')), onMouseLeave: () => setInteractionState((s) => (s === 'focus' ? s : 'default')) },
            shouldShowRatingInput ? (React.createElement("div", { className: classes.ratingInputContainer },
                React.createElement(RatingsInput, { className: classes.ratingInput, "data-hook": RATING_INPUT, value: rating?.value ?? undefined, label: ratingLabel && formatLabel(ratingLabel, rating?.isRequired), labelLayout: isMobile ? 'labelTop' : 'labelAside', size: isMobile ? 'large' : 'small', disabled: !onRatingChange, onSelect: (newRating) => {
                        const value = newRating === rating?.value ? undefined : newRating;
                        onRatingChange?.(value);
                        setIsExpanded(true);
                    } }),
                isMobile && React.createElement("div", { className: classes.gradientOverlay }))) : null,
            isExpanded ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classes.editorWrapper, onFocus: () => setInteractionState('focus'), onBlur: () => setInteractionState('default') },
                    React.createElement(RicosEditorLazy, { setRef: (ref) => (editorRef.current = ref), key: contentForm.editorKey, onChange: (content, contentTraits) => contentForm.onChange({ content, contentTraits }), onBusyChange: contentForm.onBusyChange, placeholder: placeholderText, initialContent: contentForm.content || undefined, externalToolbarId: getExternalToolbarId(resourceId, formName), onAddPluginContent: (pluginType) => {
                            pluginType === 'image'
                                ? biInitiateImageUpload({ isReply, resourceId })
                                : biInitiateFileUpload({ isReply, resourceId });
                        }, autoFocus: autoFocus.current, contentId: commentId, fallback: React.createElement(PlaceholderText, { text: placeholderText }) })),
                React.createElement(CommentBoxInnerFooter, { className: classes.innerFooter, ricosToolbarId: getExternalToolbarId(resourceId, formName), publishControls: isMobile ? renderPublishControls() : undefined }))) : (React.createElement("button", { "data-hook": COMMENT_BOX_PLACEHOLDER, className: classes.placeholder, onClick: () => {
                    biCommentFormExpanded({ resourceId });
                    setIsExpanded(true);
                } },
                rating && isMobile ? React.createElement(StarIcon, { className: classes.starIcon }) : null,
                ' ',
                React.createElement(PlaceholderText, { text: placeholderText })))),
        isExpanded && (React.createElement(CommentBoxOuterFooter, { currentMember: currentMember, publishControls: isMobile ? undefined : renderPublishControls() }))));
    return isInList ? (React.createElement(ListItemContainer, { ref: containerRef, id: id, isPending: false, isReply: isReply, dataHook: classNames(LIST_ITEM, isReply ? REPLY : TOP_LEVEL, COMMENT_BOX, dataHook), isMarked: isMarked },
        React.createElement("div", { className: st(classes.root, { isReply, isEdit, isMobile, withRating: !!rating }, className) }, innerContent))) : (React.createElement("div", { id: id, ref: containerRef, "data-hook": classNames(COMMENT_BOX, dataHook), className: st(classes.root, { isReply, isMobile, withRating: !!rating }, className) }, innerContent));
};
export default withEditorContext(CommentBox);
//# sourceMappingURL=comment-box.js.map